import { AzureAuthConfiguration, AzureAuthProvider } from "@datamole/wds-azure-auth";
import { TooltipProvider } from "@datamole/wds-component-tooltip";
import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import "@datamole/wds-css-layer";
import "@datamole/wds-css-normalize";

import { MapContext, MapTransform } from "./features/map/map.context";

import { ErrorFallback } from "@/components/ErrorFallback";
import { config } from "@/config";
import { AuthenticationGuard } from "@/features/auth/AuthenticationGuard";
import { InfoPopover } from "@/features/info-popover/info-popover";
import { InfoPopoverContext, Trigger } from "@/features/info-popover/popover.context";
import { DEFAULT_SCALE } from "@/features/map/map-transform.utils";
import { DataProvider } from "@/features/storage/data-provider";
import "@/styles/global.css";
import { AppRoutes } from "@/routes/AppRoutes";
import { store } from "@/store";

/**
 * Component representing the main application
 *
 * @returns rendered application
 */
const App = () => {
    const [trigger, setTrigger] = useState<Trigger | null>(null);
    const [transform, setTransform] = useState<MapTransform>({
        position: { x: 0, y: 0 },
        scale: DEFAULT_SCALE,
    });

    const azureAuthConfiguration: AzureAuthConfiguration = {
        authorityUrl: config.AZURE_AD_AUTH_AUTHORITY_URL,
        clientId: config.AZURE_AD_AUTH_CLIENT_ID,
        redirectUrl: config.AZURE_AD_AUTH_REDIRECT_URL,
        postLogoutRedirectUrl: config.AZURE_AD_AUTH_POST_LOGOUT_REDIRECT_URL,
    };

    return (
        <AzureAuthProvider configuration={azureAuthConfiguration}>
            <AuthenticationGuard>
                <Provider store={store}>
                    <ErrorBoundary fallback={<ErrorFallback />}>
                        <DataProvider>
                            <TooltipProvider>
                                <InfoPopoverContext.Provider value={{ trigger, setTrigger }}>
                                    <MapContext.Provider value={{ transform, setTransform }}>
                                        <BrowserRouter>
                                            <AppRoutes />
                                        </BrowserRouter>
                                        <InfoPopover />
                                    </MapContext.Provider>
                                </InfoPopoverContext.Provider>
                            </TooltipProvider>
                        </DataProvider>
                    </ErrorBoundary>
                </Provider>
            </AuthenticationGuard>
        </AzureAuthProvider>
    );
};

export { App };
